import {
  formatDateWithDynamicTimezone,
  formatIDR
} from "@marketplace/shared/utils";
import {
  Button,
  Center,
  ConditionalRendering,
  Loader,
  Modal,
  Text,
  Textarea,
  TextInput
} from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { MdCheckCircle } from "react-icons/md";

import { ProtectedPdfViewer } from "@/components";
import { usePermissions } from "@/hooks";
import { formatWithLocale } from "@/utils";

import { useGetBankAccountRevealDetailQuery } from "../../../settings/hooks/queries/use-get-bank-account-reveal-detail-query";
import { claimHistoryReviewModalAtom } from "../../atoms/claim-history-review-modal-atom";
import { usePostApproveClaimMutation } from "../../hooks/mutations/use-post-approve-claim-mutation";
import { usePostRejectClaimMutation } from "../../hooks/mutations/use-post-reject-claim-mutation";
import { useGetClaimEstimationQuery } from "../../hooks/queries/use-get-claim-estimation-query";
import { useGetClaimHistoryDetailQuery } from "../../hooks/queries/use-get-claim-history-detail-query";
import { ClaimEstimationBreakdown } from "../claim-estimation-breakdown";
import { ClaimOrderStatusTracker } from "../claim-order-status-tracker";

const isPdfFile = (filePath: string | undefined): boolean => {
  if (!filePath) return false;
  return filePath.toLowerCase().endsWith(".pdf");
};

export const ClaimHistoryReviewModal = () => {
  const { visible, publicId, isViewDetail } = useAtomValue(
    claimHistoryReviewModalAtom
  );
  const resetModal = useResetAtom(claimHistoryReviewModalAtom);
  const { t } = useTranslation("order", { keyPrefix: "claimHistoryPage" });
  const queryClient = useQueryClient();

  const { isSuperAdmin } = usePermissions();

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [masked, setMasked] = useState(true);

  const handleToggleMask = () => setMasked((prev) => !prev);

  const { data, isLoading, isError } = useGetClaimHistoryDetailQuery({
    queryParams: { publicId: publicId! },
    options: {
      enabled: visible && !!publicId,
      throwOnError: false,
      retry: false
    }
  });

  const detail = data?.data;
  const orderIds = detail?.orders?.map((o) => o.order_id) ?? [];
  const renderRejectBtn =
    isSuperAdmin && isViewDetail && detail?.current_status === "approve";

  const {
    data: revealedBankAccount,
    isFetching: isRevealing,
    isError: isRevealError
  } = useGetBankAccountRevealDetailQuery({
    bankInfoId: String(detail?.bank_account_id ?? ""),
    options: {
      enabled: !masked && visible && !!detail?.bank_account_id
    }
  });

  const accountNumber = masked
    ? detail?.account_number
    : (revealedBankAccount?.data?.account_number ?? detail?.account_number);
  const accountName = masked
    ? detail?.account_name
    : (revealedBankAccount?.data?.account_name ?? detail?.account_name);

  useEffect(() => {
    if (!masked && isRevealError) {
      notifications.show({
        variant: "error",
        message: t("reviewModal.revealFailed") as string
      });
      setMasked(true);
    }
  }, [masked, isRevealError, t]);

  useEffect(() => {
    if (!visible) {
      setMasked(true);
      queryClient.removeQueries({ queryKey: ["bankAccountRevealDetail"] });
    }
  }, [visible, queryClient]);

  const { data: estimationData, isLoading: isEstimationLoading } =
    useGetClaimEstimationQuery({
      orderIds,
      options: {
        enabled: visible && !!publicId && orderIds.length > 0,
        throwOnError: false,
        retry: false
      }
    });

  useEffect(() => {
    if (isError) {
      resetModal();
    }
  }, [isError, resetModal]);

  const { mutateAsync: approve, isPending: isApproving } =
    usePostApproveClaimMutation({
      options: {
        onSuccess: () => {
          notifications.show({ message: t("reviewModal.approveSuccess") });
          resetModal();
        },
        onError: () => {
          notifications.show({
            message: t("reviewModal.approveError"),
            variant: "error"
          });
        }
      }
    });

  const { mutateAsync: reject, isPending: isRejecting } =
    usePostRejectClaimMutation({
      options: {
        onSuccess: () => {
          notifications.show({ message: t("rejectModal.rejectSuccess") });
          setShowRejectModal(false);
          resetModal();
        },
        onError: () => {
          notifications.show({
            message: t("rejectModal.rejectError"),
            variant: "error"
          });
        }
      }
    });

  const handleApprove = async () => {
    if (!publicId) return;
    await approve({ publicId, status: "approve" });
  };

  const handleOpenReject = () => {
    setRejectionReason("");
    setShowRejectModal(true);
  };

  const handleBackToReview = () => {
    setShowRejectModal(false);
  };

  const handleConfirmRejection = async () => {
    if (!publicId || !rejectionReason.trim()) return;
    await reject({
      publicId,
      status: "reject",
      reason: rejectionReason.trim()
    });
  };

  return (
    <>
      <Modal
        opened={visible && !showRejectModal}
        onClose={resetModal}
        title={
          <div className="flex items-center justify-between w-full">
            <Text type="heading" weight="semibold">
              {t("reviewModal.title")}
            </Text>
            <Text type="text" size="xs" className="text-fjord-grey-500">
              {detail?.created_at
                ? t("reviewModal.latest", {
                    date: formatDateWithDynamicTimezone(detail.updated_at, true)
                  })
                : ""}
            </Text>
          </div>
        }
        classNames={{ title: "w-full", header: "!pb-0" }}
        size={610}
        withCloseButton
      >
        <ConditionalRendering condition={isLoading}>
          <Center>
            <Loader />
          </Center>
        </ConditionalRendering>

        <ConditionalRendering condition={!isLoading && visible}>
          <div className="space-y-6">
            <ClaimOrderStatusTracker
              publicId={publicId}
              rejectReason={detail?.reject_reason}
              currentStatus={detail?.current_status}
            />

            <div className="space-y-1">
              <Text className="text-fjord-grey-500">
                {t("reviewModal.orderList")}
              </Text>
              <div className="rounded-lg bg-mischka-100 border border-solid border-mischka-400 p-3 space-y-[14px]">
                {detail?.orders && detail.orders.length > 0 ? (
                  detail.orders.map((order) => (
                    <div
                      key={order.order_id}
                      className="grid grid-cols-[auto_1fr_1fr] items-center gap-2"
                    >
                      <MdCheckCircle
                        className="text-noble-blue-500 shrink-0"
                        size={16}
                      />
                      <Text className="text-fjord-grey-500">
                        {order.order_id}
                      </Text>
                      <Text className="text-fjord-grey-500">
                        {order.buyer_company_name}
                      </Text>
                    </div>
                  ))
                ) : (
                  <Text className="text-fjord-grey-400 text-center py-2">
                    {t("reviewModal.noOrders")}
                  </Text>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-1">
                <TextInput
                  label={t("reviewModal.invoiceNumber")}
                  size="lg"
                  value={detail?.invoice_number ?? ""}
                  readOnly
                />
              </div>
              <div className="flex-1">
                <TextInput
                  label={t("reviewModal.invoiceDate")}
                  size="lg"
                  value={
                    detail?.invoice_date
                      ? formatWithLocale(detail.invoice_date, "dd/MM/yy")
                      : ""
                  }
                  readOnly
                />
              </div>
            </div>

            <TextInput
              label={t("reviewModal.taxInvoiceNumber")}
              size="lg"
              value={detail?.tax_invoice_number ?? ""}
              readOnly
            />

            <TextInput
              label={t("reviewModal.invoiceAmount")}
              size="lg"
              value={
                detail?.invoice_amount != null
                  ? detail.invoice_amount.toLocaleString("id-ID")
                  : ""
              }
              readOnly
            />

            <ClaimEstimationBreakdown
              invoiceAmount={detail?.invoice_amount ?? 0}
              estimation={estimationData?.data?.estimation_amount}
              payoutFee={estimationData?.data?.payout_fee}
              totalProductPrice={estimationData?.data?.total_product_price}
              totalPpn={estimationData?.data?.total_ppn}
              totalPph23={estimationData?.data?.total_pph23}
              isEstimationLoading={isLoading || isEstimationLoading}
              keyPrefix="claimHistoryPage.reviewModal"
            />
            <ConditionalRendering condition={isSuperAdmin}>
              <TextInput
                label={t("reviewModal.seller")}
                size="lg"
                value={detail?.orders?.[0]?.seller_company_name ?? ""}
                readOnly
              />
            </ConditionalRendering>
            <ConditionalRendering
              condition={detail?.current_status !== "payment_success"}
            >
              <TextInput
                label={t("reviewModal.bankAccount")}
                size="lg"
                value={`${accountName} - ${accountNumber} (${detail?.bank_name})`}
                readOnly
                rightSection={
                  !masked && isRevealing ? (
                    <Loader size="sm" />
                  ) : masked ? (
                    <IoMdEye
                      size={20}
                      className="cursor-pointer text-fjord-grey-500"
                      onClick={handleToggleMask}
                    />
                  ) : (
                    <IoMdEyeOff
                      size={20}
                      className="cursor-pointer text-fjord-grey-500"
                      onClick={handleToggleMask}
                    />
                  )
                }
              />
            </ConditionalRendering>
            <ConditionalRendering
              condition={detail?.current_status === "payment_success"}
            >
              <div className="bg-mischka-100 p-4 rounded-lg">
                <div className="flex flex-col gap-4 ">
                  <div className="flex flex-col gap-2 ">
                    <div className="flex items-center gap-2">
                      <Text className="text-fjord-grey-500 ">
                        {t("reviewModal.bankAccount")}
                      </Text>
                      {!masked && isRevealing ? (
                        <Loader size="sm" />
                      ) : masked ? (
                        <IoMdEye
                          size={20}
                          className="cursor-pointer text-fjord-grey-500"
                          onClick={handleToggleMask}
                        />
                      ) : (
                        <IoMdEyeOff
                          size={20}
                          className="cursor-pointer text-fjord-grey-500"
                          onClick={handleToggleMask}
                        />
                      )}
                    </div>
                    <Text
                      className="text-fjord-grey-500 font-semibold"
                      size="xl"
                    >
                      {`${accountNumber} - ${accountName}`}
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2 ">
                    <Text className="text-fjord-grey-500 ">
                      {t("reviewModal.bankName")}
                    </Text>
                    <Text
                      className="text-fjord-grey-500 font-semibold"
                      size="xl"
                    >
                      {detail?.bank_name}
                    </Text>
                  </div>
                  <div className="flex flex-col gap-2 ">
                    <Text className="text-fjord-grey-500 ">
                      {t("reviewModal.totalDisbursement")}
                    </Text>
                    <Text
                      className="text-fjord-grey-500 font-semibold"
                      size="xl"
                    >
                      {formatIDR(detail?.final_payout_amount ?? 0)}
                    </Text>
                  </div>
                </div>
              </div>
            </ConditionalRendering>

            <div className="space-y-1">
              <Text className="text-fjord-grey-500">
                {t("reviewModal.file")}
              </Text>
              <div className="bg-fjord-grey-100 rounded-lg h-[324px] relative overflow-hidden">
                {detail?.invoice_file_path ? (
                  isPdfFile(detail.invoice_file_path) ? (
                    <ProtectedPdfViewer
                      src={`media/${detail.invoice_file_path}`}
                      className="w-full h-full"
                    />
                  ) : (
                    <Center className="h-full text-fjord-grey-400">
                      {t("reviewModal.previewNotAvailable")}
                    </Center>
                  )
                ) : (
                  <Center className="h-full text-fjord-grey-400">
                    {t("reviewModal.noFile")}
                  </Center>
                )}
              </div>
            </div>

            <ConditionalRendering condition={isSuperAdmin && !isViewDetail}>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="danger"
                  onClick={handleOpenReject}
                  className="w-[128px]"
                  disabled={isApproving}
                >
                  {t("reviewModal.reject")}
                </Button>

                <Button
                  variant="primary"
                  onClick={handleApprove}
                  loading={isApproving}
                  className="w-[128px]"
                >
                  {t("reviewModal.approveClaim")}
                </Button>
              </div>
            </ConditionalRendering>
            <ConditionalRendering condition={renderRejectBtn}>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="danger"
                  onClick={handleOpenReject}
                  className="w-[128px]"
                  disabled={isApproving}
                >
                  {t("reviewModal.reject")}
                </Button>
              </div>
            </ConditionalRendering>
          </div>
        </ConditionalRendering>
      </Modal>

      <Modal
        opened={showRejectModal && isSuperAdmin}
        onClose={handleBackToReview}
        title={
          <Text type="heading" size="l" className="text-fjord-grey-500">
            {t("rejectModal.title")}
          </Text>
        }
        size={440}
        withCloseButton
        classNames={{ body: "!pt-0" }}
      >
        <div className="space-y-6">
          <Textarea
            label={t("rejectModal.reasonLabel")}
            placeholder={t("rejectModal.reasonPlaceholder")}
            minRows={5}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
          />

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={handleBackToReview}
              disabled={isRejecting}
            >
              {t("rejectModal.backToReview")}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmRejection}
              loading={isRejecting}
              disabled={!rejectionReason.trim()}
            >
              {t("rejectModal.confirmRejection")}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
